import { api, apiBase, TOKEN_KEY } from './client';

/**
 * 🔴 金额在整条链路上都是**字符串**，前端也不例外。任何 `Number(amount)`
 * 都会把它推进 IEEE754 双精度 —— 展示、传输、比较一律用字符串。
 */

export type ImportIssueSeverity = 'error' | 'notice';

export interface ImportRowIssue {
  rowNumber: number;
  /** 列标题；整行级问题为 null。 */
  column: string | null;
  code: string;
  message: string;
  /** 🔴 `notice` 不阻塞提交（典型：这一列在这个国家用不上，已忽略）。 */
  severity: ImportIssueSeverity;
}

export interface ImportRowDraft {
  rowNumber: number;
  amount: string;
  currency: string;
  reference: string;
  note: string | null;
  feePaidBy: 'PAYER' | 'BENEFICIARY';
  swiftChargeOption: 'PAYER' | 'SHARED';
  accountName: string | null;
  bankCountryCode: string;
  entityType: 'PERSONAL' | 'COMPANY';
  nickname: string | null;
  contactEmail: string | null;
  values: Record<string, string>;
}

export interface ImportCheckedRow {
  rowNumber: number;
  /**
   * 这一行的原始单元格（key → 值）。表格显示的就是它。
   * 🔴 出错的行没有 draft，而那些行的原始值才最需要显示给人看 —— 所以显示一律走 cells。
   */
  cells: Record<string, string | null>;
  /** null = 这一行有 error，不会被创建。 */
  draft: ImportRowDraft | null;
  issues: ImportRowIssue[];
}

export interface ImportColumnHead {
  key: string;
  title: string;
}

/** 原样回传给 commit 的行。🔴 回传的是**原始单元格**，不是 draft —— 服务端从同一份原始输入重新校验。 */
export interface ImportRawRow {
  rowNumber: number;
  cells: Record<string, string | null>;
}

export interface ImportCurrencyTotal {
  currency: string;
  amount: string;
  count: number;
}

export interface ImportCheckResult {
  sourceRecordId?: string;
  /** 🔴 幂等键。提交时必须原样带回去，它是重复提交唯一挡得住的地方。 */
  token: string;
  /** 表头，顺序即列序。按 key 去 `cells` 里取值，不靠列序猜。 */
  columns: ImportColumnHead[];
  rows: ImportCheckedRow[];
  errorCount: number;
  noticeCount: number;
  totals: ImportCurrencyTotal[];
  beneficiaryPaysCount: number;
  /**
   * 解析时被当成模板家具（说明行 / 示例行）整行跳过的 Excel 行号。
   * 🔴 必须显示出来——不然用户粘 30 行进去发现只有 28 行，没有任何办法知道为什么
   * （PRODUCT.md §5.10.2）。
   */
  skippedTemplateRows: number[];
}

export interface ImportCreatedRow {
  rowNumber: number;
  payeeId: string;
  payeeShortId: string;
  applicationId: string;
}

export interface ImportCommitResult {
  batchId: string;
  /** true = 这个 token 之前提交过，本次什么都没创建。 */
  duplicated: boolean;
  created: ImportCreatedRow[];
}

/**
 * 下载模板。走裸 fetch 而不是 axios：要的是字节流 + 自己触发下载，
 * 而 axios 的拦截器与 JSON 处理在这里只会碍事。Bearer 要自己带（拦截器只作用于 axios）。
 */
export async function downloadTemplate(): Promise<void> {
  const res = await fetch(`${apiBase()}/payout-imports/template`, {
    headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY) ?? ''}` },
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
    throw new Error(body?.error?.message ?? '模板下载失败，请稍后重试');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'hipay-批量打款模板.xlsx';
  a.click();
  // 🔴 不同步 revoke：老 Safari / 慢盘上，点击触发的下载可能还没真正开始读这个 blob URL，
  // 同步 revoke 会让下载中断。挪到下一个宏任务，给浏览器一点时间先把下载起动起来
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function parseImport(file: File): Promise<ImportCheckResult> {
  const form = new FormData();
  form.append('file', file);
  // 不手写 Content-Type —— 浏览器要自己补上 multipart 的 boundary
  const { data } = await api.post<ImportCheckResult>('/payout-imports/parse', form);
  return data;
}

export async function commitImport(
  token: string,
  rows: ImportRawRow[],
  sourceRecordId?: string,
): Promise<ImportCommitResult> {
  const { data } = await api.post<ImportCommitResult>('/payout-imports/commit', { token, rows, sourceRecordId });
  return data;
}
